import React, { ReactElement, Ref, useContext, useRef } from 'react'
import { CodeAddModify } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Icon, { IconName } from '../../Common/Components/Icon'
import ModalExitPromptContext, { ModalExitPromptType } from '../../Common/Components/Modal/ModalExitPromptContext'
import PopperBubble from '../../Common/Components/PopperBubble'
import { DocTopic } from '../../Common/Constants/Documentation'
import { LanguageContext } from '../../Common/Contexts'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import { ServerErrorOptions } from '../../Common/Types/Types'
import { openHelpPortalByTopic } from '../../Common/Utils'
import { servletPath } from '../../../NonReact/common/RxJS/RxJS'
import { ServerError } from '../../Common/Components/ServerError'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'

/**
 * Component properties type.
 */
type Props = {
    isValidationPopperOpen: boolean
    closeModal: () => void
    serverError?: ServerErrorOptions[] | string
}

/**
 * Footer actions for the Add User modal.
 *
 * @param isValidationPopperOpen - Is the validation popper open.
 * @param closeModal - Close modal function.
 * @param serverError Server error string  or object.
 */
const FooterActions: React.FC<Props> = ({ isValidationPopperOpen, closeModal, serverError }: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    language.loadBundle(CodeAddModify.NAME)

    const elementRef: Ref<HTMLButtonElement> = useRef(null)

    const {
        formState: { isValid }
    }: UseFormMethods = useFormContext()

    const handleHelpClick = (): void => {
        openHelpPortalByTopic(servletPath, DocTopic.ADMINCODES)
    }

    const handleCancel = (): void => {
        if (commonForm?.isTouched) {
            displayExitPrompt()
        } else {
            closeModal()
        }
    }

    return (
        <div>
            {serverError ? <ServerError serverError={serverError} /> : <></>}
            <div className='tabs-footer'>
                <RoundedButton type='button' onClick={handleHelpClick} shrink>
                    {language.get(CodeAddModify.CodeAddModify_ButtonHelpLabel)}
                </RoundedButton>
                <RoundedButton popoverRef={elementRef} type='submit' disabled={!isValid} shrink>
                    {language.get(CodeAddModify.CodeAddModify_ButtonSubmitLabel)}
                </RoundedButton>

                <PopperBubble
                    anchorEl={elementRef.current}
                    open={isValidationPopperOpen}
                    placement='top'
                    content={
                        <div className='validation-popper-container'>
                            <Icon iconName={IconName.ERRORCIRCLE} />
                            <span>{language.get(CodeAddModify.CodeAddModify_ValidationWarning)}</span>
                        </div>
                    }
                    className='colour-red'
                />

                <RoundedButton onClick={handleCancel} isOutlined={true} type='button' shrink>
                    {language.get(CodeAddModify.CodeAddModify_ButtonCancelLabel)}
                </RoundedButton>
            </div>
        </div>
    )
}

export default FooterActions
